import {
  updateItemWatchState,
} from "@/api/endpoints";
import type { Item, PlaybackSession } from "@/types/api";

export function isPlaybackSessionCompleted(session: PlaybackSession, durationMS: number): boolean {
  if (durationMS <= 0) {
    return false;
  }
  return session.attributes.position_ms / durationMS >= 0.98;
}

export function sortPlaybackSessionsNewest(sessions: PlaybackSession[]): PlaybackSession[] {
  return [...sessions].sort((a, b) => Date.parse(b.attributes.updated_at) - Date.parse(a.attributes.updated_at));
}

export function buildPlaybackSessionsByItem(sessions: PlaybackSession[]): Map<string, PlaybackSession[]> {
  const sessionsByItem = new Map<string, PlaybackSession[]>();

  for (const session of sessions) {
    const itemId = session.relationships.item.data.id;
    const existing = sessionsByItem.get(itemId);
    if (existing) {
      existing.push(session);
    } else {
      sessionsByItem.set(itemId, [session]);
    }
  }

  for (const [itemId, itemSessions] of sessionsByItem.entries()) {
    sessionsByItem.set(itemId, sortPlaybackSessionsNewest(itemSessions));
  }

  return sessionsByItem;
}

export function completedPlaybackSessionForItem(item: Item, sessions: PlaybackSession[]): PlaybackSession | null {
  const durationMS = item.attributes.duration_ms ?? 0;
  if (durationMS <= 0) {
    return null;
  }
  return sessions.find((session) => isPlaybackSessionCompleted(session, durationMS)) ?? null;
}

export async function markItemPlayed(
  itemId: string,
  options: { preserveSessionId?: string | null } = {},
): Promise<void> {
  if (options.preserveSessionId?.trim()) {
    await updateItemWatchState(itemId, true, options);
    return;
  }
  await updateItemWatchState(itemId, true);
}

export async function clearItemPlaybackState(
  itemId: string,
  options: { preserveSessionId?: string | null } = {},
): Promise<void> {
  if (options.preserveSessionId?.trim()) {
    await updateItemWatchState(itemId, false, options);
    return;
  }
  await updateItemWatchState(itemId, false);
}
